import * as Sentry from '@sentry/react';

import { track } from './analytics';

/**
 * A frame that blocks the main thread for longer than this is perceived by the
 * user as jank; past ~500ms it reads as a freeze.
 */
const FREEZE_THRESHOLD_MS = 200;

/**
 * Freezes often repeat in a render loop. Cap reporting so one bad session
 * cannot flood the analytics pipeline.
 */
const MAX_REPORTS_PER_SESSION = 25;

/**
 * `long-animation-frame` entries are not in TypeScript's DOM lib yet.
 * https://developer.mozilla.org/en-US/docs/Web/API/PerformanceLongAnimationFrameTiming
 */
interface LongAnimationFrameScript {
  duration: number;
  invoker: string;
  invokerType: string;
  sourceURL: string;
  sourceFunctionName: string;
}

interface LongAnimationFrameTiming extends PerformanceEntry {
  blockingDuration: number;
  scripts: LongAnimationFrameScript[];
}

const getLongestScript = (
  entry: LongAnimationFrameTiming,
): LongAnimationFrameScript | undefined =>
  entry.scripts.reduce<LongAnimationFrameScript | undefined>(
    (longest, script) =>
      !longest || script.duration > longest.duration ? script : longest,
    undefined,
  );

const HTTP_URL = /^https?:\/\//;

/**
 * Drops the query string and fragment from a script URL before it is reported.
 * Google Tag Manager injects third-party tags configured outside this repo, so
 * their URL parameters are not ours to vouch for, and this telemetry goes to
 * three external services. Origin and path identify the blocking asset.
 *
 * Only HTTP(S) values are trimmed: `invoker` is also an element reference for
 * event handlers (`IMG#logo.onload`), where the fragment is the diagnostic.
 * @param {string | undefined} value Script URL or invoker string.
 * @return {string | undefined} The value without query string or fragment.
 */
const withoutUrlParams = (value: string | undefined): string | undefined =>
  value && HTTP_URL.test(value) ? value.split('#')[0].split('?')[0] : value;

const isSupported = (): boolean =>
  typeof PerformanceObserver !== 'undefined' &&
  PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame');

/**
 * Reports main-thread freezes to Mixpanel (product analytics) and to Sentry as
 * a log (queryable, and attached to the trace so it shows in context).
 *
 * Sentry's own `ui.long-animation-frame` spans already record that a frame was
 * slow; what they do not expose is which script blocked it, which is why this
 * reports the blocking script itself.
 *
 * Chromium-only — other browsers do not implement `long-animation-frame` yet.
 * @return {() => void} Stops the observer.
 */
export function startFreezeMonitor(): () => void {
  if (!isSupported()) return () => {};

  let reportCount = 0;

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries() as LongAnimationFrameTiming[]) {
      if (entry.duration < FREEZE_THRESHOLD_MS) continue;
      if (reportCount >= MAX_REPORTS_PER_SESSION) return;
      reportCount++;

      const longestScript = getLongestScript(entry);
      const properties = {
        duration_ms: Math.round(entry.duration),
        blocking_duration_ms: Math.round(entry.blockingDuration),
        pathname: window.location.pathname,
        script_source: withoutUrlParams(longestScript?.sourceURL),
        script_function: longestScript?.sourceFunctionName,
        script_invoker: withoutUrlParams(longestScript?.invoker),
        script_duration_ms: longestScript
          ? Math.round(longestScript.duration)
          : undefined,
      };

      track('web_app_main_thread_freeze', properties);
      Sentry.logger.warn(
        `Main thread blocked for ${properties.duration_ms}ms`,
        properties,
      );
    }
  });

  observer.observe({ type: 'long-animation-frame', buffered: true });

  return () => observer.disconnect();
}
